// Slider / Banner / Popup (MOD-10, ENT-08, config-tier) types.
//
// Üç benzer "site bileşeni" tek feature klasöründe toplanır. BE'nin ürettiği
// wire DTO'ları kullanılır; UI için garanti alanlı FE tiplerine map'lenir.
// Config-tier iki-durum publish (Draft↔Published); tarih-aktif pencere
// (startDate/endDate); Popup ayrıca gösterim sıklığı taşır. Yerleşim/sıklık
// değerleri BE sabitlerini birebir aynalar.

import type { components } from '@/types/api';

/** Wire DTO'ları (generated). */
export type SliderDtoWire = components['schemas']['SliderDto'];
export type SliderListItemDtoWire = components['schemas']['SliderListItemDto'];
export type SliderImageDtoWire = components['schemas']['SliderImageDto'];
export type CreateSliderRequest = components['schemas']['CreateSliderRequest'];
export type UpdateSliderRequest = components['schemas']['UpdateSliderRequest'];
export type CreateSliderImageRequest = components['schemas']['CreateSliderImageRequest'];
export type UpdateSliderImageRequest = components['schemas']['UpdateSliderImageRequest'];
export type SliderImageOrderWire = components['schemas']['SliderImageOrder'];

export type BannerDtoWire = components['schemas']['BannerDto'];
export type BannerListItemDtoWire = components['schemas']['BannerListItemDto'];
export type CreateBannerRequest = components['schemas']['CreateBannerRequest'];
export type UpdateBannerRequest = components['schemas']['UpdateBannerRequest'];

export type PopupDtoWire = components['schemas']['PopupDto'];
export type PopupListItemDtoWire = components['schemas']['PopupListItemDto'];
export type CreatePopupRequest = components['schemas']['CreatePopupRequest'];
export type UpdatePopupRequest = components['schemas']['UpdatePopupRequest'];

/** Slider/Banner yerleşimi — BE `SliderPlacements` sabitiyle birebir. */
export const WIDGET_PLACEMENTS = ['home_hero', 'home_secondary', 'sidebar', 'campaigns'] as const;
export type WidgetPlacement = (typeof WIDGET_PLACEMENTS)[number];

/** Popup gösterim sıklığı — BE `PopupFrequencies` sabitiyle birebir. */
export const POPUP_FREQUENCIES = ['once', 'session', 'daily'] as const;
export type PopupFrequency = (typeof POPUP_FREQUENCIES)[number];

/** Config-tier iki-durum yayın durumu. */
export type ConfigStatus = 'Draft' | 'Published';

export function normalizeConfigStatus(status: string | null | undefined): ConfigStatus {
  return status === 'Published' ? 'Published' : 'Draft';
}

// --- Slider ---

export interface SliderListItem {
  id: string;
  name: string;
  placement: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  imageCount: number;
  updatedAt: string;
}

export interface SliderImage {
  id: string;
  sliderId: string;
  imageUrl: string;
  title: string | null;
  subtitle: string | null;
  ctaText: string | null;
  ctaLink: string | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

export interface Slider {
  id: string;
  name: string;
  placement: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  variantLanguageCodes: string[];
  images: SliderImage[];
}

export interface SliderCreateInput {
  name: string;
  placement: string;
  languageId?: string | null;
}

export interface SliderUpdateInput {
  name: string;
  placement: string;
}

export interface SliderImageInput {
  imageUrl: string;
  title?: string | null;
  subtitle?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  order?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  isActive: boolean;
}

// --- Banner ---

export interface BannerListItem {
  id: string;
  name: string;
  placement: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  order: number;
  startDate: string | null;
  endDate: string | null;
  updatedAt: string;
}

export interface Banner {
  id: string;
  name: string;
  placement: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  imageUrl: string | null;
  title: string | null;
  ctaText: string | null;
  ctaLink: string | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  variantLanguageCodes: string[];
}

export interface BannerInput {
  name: string;
  placement: string;
  imageUrl?: string | null;
  title?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  order?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  languageId?: string | null;
}

// --- Popup ---

export interface PopupListItem {
  id: string;
  name: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  frequency: string;
  startDate: string | null;
  endDate: string | null;
  updatedAt: string;
}

export interface Popup {
  id: string;
  name: string;
  languageId: string;
  groupId: string;
  status: ConfigStatus;
  imageUrl: string | null;
  title: string | null;
  body: string | null;
  ctaText: string | null;
  ctaLink: string | null;
  targetPages: string | null;
  frequency: string;
  startDate: string | null;
  endDate: string | null;
  variantLanguageCodes: string[];
}

export interface PopupInput {
  name: string;
  frequency: string;
  imageUrl?: string | null;
  title?: string | null;
  body?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  targetPages?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  languageId?: string | null;
}
